// The user-defined "Best for" score — a RECIPE, not a formula language.
//
// The five baked activity scores all share one shape (see src/lib/derive.js):
// a gate trapezoid on feels-like or wind sets the ceiling, and each spoiler
// multiplies in as (1 - w + w * factor) so nothing but the gate can zero the
// score. A recipe is just that shape with the knobs exposed: which variable
// gates, where the plateau sits, and how much each spoiler weighs. Constraining
// custom scores to this template is deliberate — every recipe reads on the same
// Poor/Fair/Good/Ideal scale as the built-ins, and the server can evaluate an
// untrusted one in microseconds with no parsing beyond a regex.
//
// WHERE it is evaluated is the honest-labelling part. The baked scores are
// mean-of-scores: computed per station-year before blending, because a
// nonlinear function of an average misdescribes a variable climate (derive.ts
// explains why). A custom score cannot afford that — it would mean re-running
// the whole bake per edit — so it is score-of-MEANS, evaluated at request time
// from the already-blended component grids (app, ws, rain, sky, v). The `dry`
// factor is exact anyway (the rain grid IS the wet-year frequency), but the
// gate trapezoid of a mean feels-like softens where years disagree. Acceptable
// for a personal layer; the help text says so.
//
// This file lives in src/lib/ so both halves import one copy: the browser scores
// city dots and series with it, the server scores field tiles — same trick as
// variables.ts. derive.ts pulls ramp/trap/NEUTRAL from here so the primitives
// cannot drift between the baked and the custom path.

// Clamped linear rise from a to b, and the plateau built from two of them.
export const ramp = (x: number, a: number, b: number): number => (x <= a ? 0 : x >= b ? 1 : (x - a) / (b - a));
export const trap = (x: number, a: number, b: number, c: number, d: number): number => Math.min(ramp(x, a, b), 1 - ramp(x, c, d));

import { isFiniteNumber } from './finite.ts';

// Neutral spoiler fallbacks when an input is undefined — most station-days are
// dry and clear, so the neutrals lean benign. Shared with deriveStation, which
// applies the same philosophy per year; here they cover the rare grid cell where
// a sparse component (rain/sky/v, 300 km cutoff) is UNDEF while the dense gate
// (app/ws) is not. The gate is never neutralised — no gate value, no score.
export const NEUTRAL = { dry: 0.8, sunny: 0.85, clear: 0.9, calm: 0.7 };

// --- schema -----------------------------------------------------------------
// Weights are coarse on purpose — integer percent snapped to 5s — so the space
// of distinct recipes (and so of server cache keys) is finite and small. Two
// weights are forced to zero because they contradict their gate: `warm`
// duplicates an 'app' gate, and `calm` fights a 'ws' gate that WANTS wind.

// The variable whose trapezoid gates the score.
export type Gate = 'app' | 'ws';

// The five spoilers, in payload order.
export type WeightKey = (typeof WEIGHT_KEYS)[number];

// Spoiler weights, integer percent 0..100 snapped to 5s.
export type Weights = Record<WeightKey, number>;

// A canonical recipe: what clampRecipe returns and every other function assumes.
export interface Recipe {
  gate: Gate;
  /** [a, b, c, d], sorted — whole °C for 'app', m/s to one decimal for 'ws'. */
  trap: number[];
  w: Weights;
}

// A recipe-shaped value from a URL, from storage or from a caller's literal,
// before clampRecipe has proven any of it.
export interface RecipeInput {
  gate?: unknown;
  trap?: unknown[];
  w?: Partial<Record<WeightKey, unknown>>;
}

// One point's component values — a grid node, a city dot's properties, or one
// slot of a series. Any of them may be missing.
export interface Conditions {
  app?: number | null;
  ws?: number | null;
  rain?: number | null;
  sky?: number | null;
  v?: number | null;
}

// One driver chip in the registry's factors[] format: the component, its label,
// the reason it can fail, and the good/ok bands (an open end is null).
export interface Factor {
  v: string;
  label: string;
  low?: string;
  high?: string;
  good: [number | null, number | null];
  ok: [number | null, number | null];
}

const APP_LO = -45; // the app grid's quantization domain (variables.ts)
const APP_HI = 50;
const WS_LO = 0;
const WS_HI = 30;

// The built-in formulas, restated as recipes — the editor's "Reset to" row,
// and the parity pin in recipe.test.js that proves the restatement is faithful.
export const PRESETS = {
  beach: { gate: 'app', trap: [22, 26, 32, 36], w: { dry: 50, calm: 40, sunny: 30, clear: 0, warm: 0 } },
  relax: { gate: 'app', trap: [12, 16, 26, 30], w: { dry: 50, calm: 25, sunny: 0, clear: 0, warm: 0 } },
  hike: { gate: 'app', trap: [2, 10, 20, 27], w: { dry: 60, calm: 30, sunny: 0, clear: 0, warm: 0 } },
  sail: { gate: 'ws', trap: [2, 3.5, 8, 11], w: { dry: 20, calm: 0, sunny: 0, clear: 40, warm: 60 } },
} satisfies Record<string, Recipe>;

export const DEFAULT_RECIPE = PRESETS.hike;

const WEIGHT_KEYS = ['dry', 'calm', 'sunny', 'clear', 'warm'] as const;

const clampNum = (x: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, x));

// Force a recipe into canonical form: gate known, trap sorted/rounded/clamped
// to the gate's domain, weights snapped to 5s, the gate-contradicting weight
// zeroed. Every path into the system runs through this — the encoder assumes
// its output, and the server's cache is keyed on the re-encoding, so two
// spellings of one recipe cannot occupy two cache entries.
export function clampRecipe(r: RecipeInput | null | undefined): Recipe {
  const gate = r && r.gate === 'ws' ? 'ws' : 'app';
  const lo = gate === 'ws' ? WS_LO : APP_LO;
  const hi = gate === 'ws' ? WS_HI : APP_HI;
  // 'app' trap in whole degrees; 'ws' to one decimal (a 3.5 m/s edge is real).
  const snap = gate === 'ws' ? (x: number) => Math.round(x * 10) / 10 : Math.round;
  const rawTrap = Array.isArray(r && r.trap) ? r!.trap! : [];
  const trapOut = [0, 1, 2, 3].map((i) => {
    const x = Number(rawTrap[i]);
    return snap(clampNum(Number.isFinite(x) ? x : lo, lo, hi));
  });
  trapOut.sort((a, b) => a - b);
  const w = {} as Weights;
  for (const k of WEIGHT_KEYS) {
    const x = Number(r && r.w ? r.w[k] : 0);
    w[k] = Number.isFinite(x) ? clampNum(Math.round(x / 5) * 5, 0, 100) : 0;
  }
  if (gate === 'app') w.warm = 0;
  else w.calm = 0;
  return { gate, trap: trapOut, w };
}

// --- authoring a recipe from a day you liked ---------------------------------
// The other way into a recipe: instead of pushing sliders, name a day that was
// right — a past holiday, a place and a time of year — and restate ITS
// conditions as the recipe that would call such a day ideal. The result is an
// ordinary recipe, so it lands in the same editor, paints the same field, and
// can be widened or re-weighted afterwards like any other.
//
// Two rules keep it honest:
//   the plateau brackets the day's own feels-like (±PLATEAU), so "ideal" means
//   "about as warm as that was", with `tolerance` setting how far either side
//   still counts;
//   a spoiler is weighted by how much the day actually DELIVERED it. A dry,
//   still, bright day asks for dry, still and bright; a day you loved in spite
//   of the drizzle does not pretend dryness was the point.
const PLATEAU = 2; // ±°C that still reads as "about this warm"
// Each spoiler's weight ramps from full at its best value to zero by `zero`.
const SPOILER_FROM_DAY = {
  dry: { read: (c: Conditions) => c.rain, zero: 60, max: 60 },
  calm: { read: (c: Conditions) => c.ws, zero: 10, max: 40 },
  sunny: { read: (c: Conditions) => c.sky, zero: 80, max: 40 },
};

export function recipeFromConditions(cond: Conditions | null | undefined, { tolerance = 4 }: { tolerance?: number } = {}): Recipe | null {
  // Number(null) is 0, which would pass a bare isFinite — check the object first.
  if (!cond || typeof cond !== 'object') return null;
  const app = Number(cond.app);
  if (!Number.isFinite(app)) return null; // no feels-like, no gate, no recipe
  const mid = Math.round(app);
  const tol = Math.max(1, Math.round(tolerance));
  const w = { dry: 0, calm: 0, sunny: 0, clear: 0, warm: 0 };
  for (const [k, s] of Object.entries(SPOILER_FROM_DAY)) {
    const x = Number(s.read(cond));
    // An unmeasured component earns a middling weight rather than a zero: the
    // day may well have been dry, we just cannot say.
    if (!Number.isFinite(x)) {
      w[k as WeightKey] = Math.round(s.max / 2 / 5) * 5;
      continue;
    }
    w[k as WeightKey] = s.max * Math.max(0, 1 - Math.min(1, x / s.zero));
  }
  // clampRecipe snaps the weights to 5s, sorts and clamps the trap, and zeroes
  // the gate-contradicting weight — this only has to get the shape right.
  return clampRecipe({ gate: 'app', trap: [mid - PLATEAU - tol, mid - PLATEAU, mid + PLATEAU, mid + PLATEAU + tol], w });
}

// --- wire format ------------------------------------------------------------
// <gate>.<a>_<b>_<c>_<d>.<dry>_<calm>_<sunny>_<clear>_<warm>
// e.g. a.2_10_20_27.60_30_0_0_0  — short enough for a shareable URL, canonical
// enough to BE the cache key. Wind trap values ride as tenths of m/s so the
// string stays all-integer.
const WIRE_RE = /^[aw]\.-?\d{1,3}(_-?\d{1,3}){3}\.\d{1,3}(_\d{1,3}){4}$/;
export const RECIPE_MAX_LEN = 48;

// Assumes a clamped recipe (every internal caller holds that); feed it anything
// else and the decode side's clamp still repairs it, at worst under a second
// cache key — which is why the server re-encodes before keying.
export function encodeRecipe(r: Recipe): string {
  const tenths = r.gate === 'ws';
  const t = r.trap.map((x) => (tenths ? Math.round(x * 10) : Math.round(x)));
  const w = WEIGHT_KEYS.map((k) => r.w[k]);
  return `${r.gate === 'ws' ? 'w' : 'a'}.${t.join('_')}.${w.join('_')}`;
}

// Parse a wire string back to a recipe object, or null if it is not one. Shape
// validation only — range and canonical form are clampRecipe's job, so the one
// true pipeline is decode -> clamp -> (re-encode when a key is needed).
export function decodeRecipe(s: unknown): Recipe | null {
  if (typeof s !== 'string' || s.length > RECIPE_MAX_LEN || !WIRE_RE.test(s)) return null;
  const [g, trapPart, wPart] = s.split('.');
  const gate = g === 'w' ? 'ws' : 'app';
  const scale = gate === 'ws' ? 0.1 : 1;
  const trap = trapPart.split('_').map((x) => Number(x) * scale);
  const wNums = wPart.split('_').map(Number);
  const w = Object.fromEntries(WEIGHT_KEYS.map((k, i) => [k, wNums[i]])) as Weights;
  return { gate, trap, w };
}

// --- evaluation -------------------------------------------------------------
// One recipe against one point's component values — a grid node, a city dot's
// properties, or one slot of a series. Inputs may be null/undefined/NaN; the
// gate refuses (null score, cell stays transparent) and spoilers fall back
// neutral, exactly the coverage rule deriveStation applies per year.
export function evalRecipe(values: Conditions, r: Recipe): number | null {
  const { app, ws, rain, sky, v } = values;
  const gateVal = r.gate === 'ws' ? ws : app;
  if (!isFiniteNumber(gateVal)) return null;
  const gate = trap(gateVal, r.trap[0], r.trap[1], r.trap[2], r.trap[3]);
  const dry = isFiniteNumber(rain) ? 1 - rain / 100 : NEUTRAL.dry;
  const calm = isFiniteNumber(ws) ? 1 - ramp(ws, 6, 12) : NEUTRAL.calm;
  const sunny = isFiniteNumber(sky) ? 1 - ramp(sky / 12.5, 4, 7) : NEUTRAL.sunny;
  const clear = isFiniteNumber(v) ? ramp(v, 1000, 4000) : NEUTRAL.clear;
  // Sail's wetsuit ramp. No published neutral exists for it (the baked score
  // gates on app instead); 0.5 is the indifferent midpoint, and in practice the
  // app grid's 800 km cutoff means it is defined wherever the wind is.
  const warm = isFiniteNumber(app) ? ramp(app, 4, 14) : 0.5;
  const f = (wPct: number, x: number) => 1 - wPct / 100 + (wPct / 100) * x;
  return (
    100 *
    gate *
    f(r.w.dry, dry) *
    f(r.w.calm, calm) *
    f(r.w.sunny, sunny) *
    f(r.w.clear, clear) *
    (r.gate === 'ws' ? f(r.w.warm, warm) : 1)
  );
}

// --- tooltip factors --------------------------------------------------------
// Restate a recipe as the registry's factors[] format so the driver chips, the
// calendar detail and the activity help render a custom score exactly like a
// built-in one. The gate's bands fall straight out of its trapezoid — plateau
// is `good`, feet are `ok` — and the weighted spoilers reuse the fixed bands
// the built-in factors already use for the same components. Weight-0 spoilers
// are omitted: a factor the score ignores is not a driver.
const SPOILER_FACTORS: Record<WeightKey, Factor> = {
  dry: { v: 'rain', label: 'dry', high: 'often wet', good: [0, 20], ok: [0, 55] },
  calm: { v: 'ws', label: 'light wind', high: 'windy', good: [0, 6], ok: [0, 14] },
  sunny: { v: 'sky', label: 'sunny', high: 'cloudy', good: [0, 30], ok: [0, 75] },
  clear: { v: 'v', label: 'clear view', low: 'poor visibility', good: [4000, null], ok: [1500, null] },
  warm: { v: 'app', label: 'warm enough', low: 'too cold', good: [14, null], ok: [6, null] },
};

export function factorsForRecipe(r: Recipe): Factor[] {
  const [a, b, c, d] = r.trap;
  const gate: Factor =
    r.gate === 'ws'
      ? { v: 'ws', label: 'right breeze', low: 'too calm', high: 'too strong', good: [b, c], ok: [a, d] }
      : { v: 'app', label: 'right feel', low: 'too cold', high: 'too hot', good: [b, c], ok: [a, d] };
  const out: Factor[] = [gate];
  for (const k of WEIGHT_KEYS) {
    if (r.w[k] > 0) out.push(SPOILER_FACTORS[k]);
  }
  return out;
}
